// Login screen. Renders when /api/me returns 401 (i.e., no valid
// Supabase session and no dev-bypass). Posts to /auth/send to mint a
// magic link; the link in the email lands on /auth/verify which the
// worker handles (sets cookies, redirects to /).
//
// Pattern adapted from Cambonic_SaaS/src/components/Login.tsx:
//   - same idle / sending / sent / error state machine
//   - no OAuth providers (single-tenant, magic-link only)
//   - GutterInventory house tokens (Tailwind 3 + HSL vars)
//   - "Inventory & Crew Management" wordmark, "Powered by Cambonic"

import { useState, type FormEvent } from 'react';
import { classifySendOutcome } from '../lib/send-outcome.js';

type SendState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent' }
  // 5xx / network failure: the link MAY have been sent — we can't confirm,
  // so we neither claim success nor cry a hard failure (and never retry,
  // since /auth/send dispatches an email).
  | { kind: 'indeterminate' }
  | { kind: 'error'; message: string };

export function Login() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<SendState>({ kind: 'idle' });

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes('@')) {
      setState({
        kind: 'error',
        message: 'Please enter a valid email address.',
      });
      return;
    }

    setState({ kind: 'sending' });

    try {
      const res = await fetch('/auth/send', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      const outcome = classifySendOutcome({ ok: res.ok, status: res.status });
      if (outcome === 'sent') {
        setState({ kind: 'sent' });
      } else if (outcome === 'error') {
        // Definitive 400/403 — surface the server's reason (unauthorized
        // email / bad address). NO retry: /auth/send sends an email.
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setState({
          kind: 'error',
          message: data.error ?? `Request failed (${res.status})`,
        });
      } else {
        setState({ kind: 'indeterminate' });
      }
    } catch {
      // Thrown fetch — the request may never have reached the server, so we
      // can't confirm the link was sent. Don't retry; show indeterminate.
      setState({ kind: 'indeterminate' });
    }
  }

  // Color tokens match docs/email-templates/magic-link.html exactly so
  // the email → login → confirm page flow looks like one coherent
  // surface. NOT the global `--primary` (steel blue, used in operational
  // UI elsewhere) — this is a deliberate brand-marketing surface that
  // sits next to the email and the auth/verify confirm page.
  //   navy:   rgb(var(--color-navy))     bronze: rgb(var(--color-bronze))
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md">
        {/* Wordmark — matches email eyebrow exactly */}
        <p className="mb-10 text-center text-[18px] font-semibold uppercase tracking-[0.14em] text-[rgb(var(--color-bronze))]">
          Inventory &amp; Crew Management
        </p>

        {state.kind === 'sent' ? (
          <div className="relative overflow-hidden rounded-lg border border-border bg-card p-8 text-center">
            <div
              aria-hidden="true"
              className="absolute left-0 right-0 top-0 h-1 bg-[rgb(var(--color-bronze))]"
            />
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="mx-auto mb-4 text-[rgb(var(--color-bronze))]"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M8 12.5l3 3 5-6" />
            </svg>
            <h2 className="mb-2 font-display text-2xl text-foreground">
              Check your inbox
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              If an account matches that address, you&rsquo;ll receive a
              sign-in link shortly. The link expires in 1 hour.
            </p>
            <p className="mt-6 text-xs text-taupe">
              Didn&rsquo;t get it? Check spam, or{' '}
              <button
                type="button"
                onClick={() => setState({ kind: 'idle' })}
                className="cursor-pointer underline hover:text-[rgb(var(--color-bronze))]"
              >
                try a different email
              </button>
              .
            </p>
          </div>
        ) : state.kind === 'indeterminate' ? (
          <div className="relative overflow-hidden rounded-lg border border-border bg-card p-8 text-center">
            <div
              aria-hidden="true"
              className="absolute left-0 right-0 top-0 h-1 bg-[rgb(var(--color-bronze))]"
            />
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="mx-auto mb-4 text-[rgb(var(--color-bronze))]"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4" />
              <path d="M12 16h.01" />
            </svg>
            <h2 className="mb-2 font-display text-2xl text-foreground">
              Couldn&rsquo;t confirm
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              We couldn&rsquo;t confirm your sign-in link was sent. Check your
              inbox &mdash; if nothing arrives in a minute, try again.
            </p>
            <p className="mt-6 text-xs text-taupe">
              <button
                type="button"
                onClick={() => setState({ kind: 'idle' })}
                className="cursor-pointer underline hover:text-[rgb(var(--color-bronze))]"
              >
                Try again
              </button>
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-xs font-medium text-muted-foreground"
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={state.kind === 'sending'}
                placeholder="you@company.com"
                className="h-11 w-full rounded-md border border-input bg-card px-4 text-foreground placeholder:text-taupe focus:border-[rgb(var(--color-bronze))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-bronze)/0.2)] disabled:opacity-50"
              />
            </div>
            <button
              type="submit"
              disabled={state.kind === 'sending'}
              className="h-11 w-full rounded-md bg-[rgb(var(--color-navy))] text-sm font-semibold text-white transition-colors hover:bg-[rgb(var(--color-navy)/0.9)] disabled:opacity-60"
            >
              {state.kind === 'sending' ? 'Sending…' : 'Send sign-in link'}
            </button>
            {state.kind === 'error' && (
              <p
                role="alert"
                className="text-center text-sm text-destructive"
              >
                {state.message}
              </p>
            )}
          </form>
        )}
      </div>

      <div className="mt-16 text-center">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-taupe">
          Powered by
        </div>
        <div className="mt-1 text-[13px] font-semibold tracking-tight text-[rgb(var(--color-bronze))]">
          Cambonic
        </div>
      </div>
    </main>
  );
}
